<template>
  <div
    :class="['email-component', 'payment-summary-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="payment-summary-wrapper">
      <!-- Summary rows -->
      <div class="summary-rows">
        <!-- Subtotal -->
        <div class="summary-row" :style="rowStyles">
          <span class="summary-label">{{ getLabel('subtotal') }}</span>
          <span class="summary-value">{{subtotalValue}}</span>
        </div>

        <!-- Discounts (if any) -->
        <div v-if="discountValue" class="summary-row" :style="rowStyles">
          <span class="summary-label">{{ getLabel('discount') }}</span>
          <span class="summary-value discount-value">-{{discountValue}}</span>
        </div>

        <!-- Fees -->
        <div v-if="feeValue" class="summary-row" :style="rowStyles">
          <span class="summary-label">{{ getLabel('fee') }}</span>
          <span class="summary-value">{{feeValue}}</span>
        </div>

        <!-- Taxes -->
        <div v-if="taxValue" class="summary-row" :style="rowStyles">
          <span class="summary-label">{{ getLabel('tax') }}</span>
          <span class="summary-value">{{taxValue}}</span>
        </div>
      </div>

      <!-- Divider -->
      <div class="summary-divider" :style="dividerStyles"></div>

      <!-- Total -->
      <div class="summary-row total-row" :style="totalRowStyles">
        <span class="summary-label">{{ getLabel('total') }}</span>
        <span class="summary-value">{{totalValue}}</span>
      </div>

      <!-- Amount Paid and Due (if enabled) -->
      <div v-if="componentData.showPaymentStatus" class="payment-status-section">
        <div class="summary-row" :style="paymentStatusStyles">
          <span class="summary-label" :style="{color: componentData.styles?.amountPaidColor || '#10b981'}">{{ getLabel('amountPaid') }}</span>
          <span class="summary-value" :style="{color: componentData.styles?.amountPaidColor || '#10b981'}">{{amountPaidValue}}</span>
        </div>
        <div class="summary-row" :style="paymentStatusStyles">
          <span class="summary-label" :style="{color: componentData.styles?.amountDueColor || '#ef4444'}">{{ getLabel('amountDue') }}</span>
          <span class="summary-value" :style="{color: componentData.styles?.amountDueColor || '#ef4444'}">{{amountDueValue}}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentSummaryComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      // These are placeholder values - in production they'd come from the system
      subtotalValue: '$174.00',
      discountValue: '',
      feeValue: '$8.70',
      taxValue: '',
      totalValue: '$182.70',
      amountPaidValue: '$0.00',
      amountDueValue: '$182.70',

      labels: {
        english: {
          subtotal: 'Subtotal',
          discount: 'Discount',
          fee: 'Fee',
          tax: 'Tax',
          total: 'Total',
          amountPaid: 'Amount paid',
          amountDue: 'Amount due'
        },
        spanish: {
          subtotal: 'Subtotal',
          discount: 'Descuento',
          fee: 'Cargo',
          tax: 'Impuesto',
          total: 'Total',
          amountPaid: 'Cantidad pagada',
          amountDue: 'Cantidad adeudada'
        },
        french: {
          subtotal: 'Sous-total',
          discount: 'Remise',
          fee: 'Frais',
          tax: 'Taxe',
          total: 'Total',
          amountPaid: 'Montant payé',
          amountDue: 'Montant dû'
        },
        swedish: {
          subtotal: 'Delsumma',
          discount: 'Rabatt',
          fee: 'Avgift',
          tax: 'Skatt',
          total: 'Totalt',
          amountPaid: 'Betalt belopp',
          amountDue: 'Att betala'
        },
        danish: {
          subtotal: 'Subtotal',
          discount: 'Rabat',
          fee: 'Gebyr',
          tax: 'Moms',
          total: 'I alt',
          amountPaid: 'Betalt beløb',
          amountDue: 'Til betaling'
        },
        greek: {
          subtotal: 'Μερικό σύνολο',
          discount: 'Έκπτωση',
          fee: 'Χρέωση',
          tax: 'Φόρος',
          total: 'Σύνολο',
          amountPaid: 'Ποσό που πληρώθηκε',
          amountDue: 'Οφειλόμενο ποσό'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    rowStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: `${styles.fontSize || '14'}px`,
        color: styles.textColor || '#666666',
        marginBottom: '8px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    totalRowStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: `${styles.totalFontSize || '18'}px`,
        fontWeight: '700',
        color: styles.totalColor || '#111827',
        marginTop: '12px',
        marginBottom: '12px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    paymentStatusStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: `${styles.fontSize || '14'}px`,
        fontWeight: '500',
        marginBottom: '8px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    dividerStyles() {
      const styles = this.componentData.styles || {}
      return {
        borderTop: `1px solid ${styles.dividerColor || '#d1d5db'}`,
        marginTop: '12px',
        marginBottom: '0'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    }
  }
}
</script>

<style scoped>
.payment-summary-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.payment-summary-component:hover,
.payment-summary-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.payment-summary-wrapper {
  width: 100%;
}

.summary-rows {
  width: 100%;
}

.summary-row {
  width: 100%;
}

.summary-label {
  flex: 1;
  text-align: left;
}

.summary-value {
  text-align: right;
  font-weight: 500;
  white-space: nowrap;
}

.discount-value {
  color: #10b981;
}

.payment-status-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

@media (max-width: 600px) {
  .summary-row {
    font-size: 13px !important;
  }

  .total-row {
    font-size: 16px !important;
  }
}
</style>
